import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowLeft, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';

// ============================================
// TARAANAYA STARS - Properly Centered
// ============================================
const Stars = () => (
  <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
    <svg width="120" height="32" viewBox="0 0 200 50">
      <defs>
        <linearGradient id="starGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#92FFF7" />
          <stop offset="50%" stopColor="#74B3FF" />
          <stop offset="100%" stopColor="#5F42C9" />
        </linearGradient>
        <filter id="starGlow"><feGaussianBlur stdDeviation="2"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      </defs>
      <path d="M 30 40 Q 65 16, 100 10 T 170 40" stroke="url(#starGrad)" strokeWidth="2" fill="none" filter="url(#starGlow)" opacity="0.85"/>
      {[[30,40,5,'#92FFF7'],[65,16,7,'#82D9F9'],[100,10,9,'#74B3FF'],[135,16,7,'#6A7BE8'],[170,40,5,'#5F42C9']].map(([x,y,s,c],i)=>(
        <path key={i} d={`M${x} ${y-s}L${x+s*.35} ${y-s*.35}L${x+s} ${y}L${x+s*.35} ${y+s*.35}L${x} ${y+s}L${x-s*.35} ${y+s*.35}L${x-s} ${y}L${x-s*.35} ${y-s*.35}Z`} fill={c} filter="url(#starGlow)"/>
      ))}
    </svg>
  </div>
);

// ============================================
// TARAANAYA LOGO - Centered
// ============================================
const Logo = ({ size = 24 }) => (
  <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', fontFamily: "'Dancing Script', cursive", fontSize: size, background: 'linear-gradient(90deg, #92FFF7, #74B3FF, #5F42C9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'blur(12px)', opacity: 0.5 }}>taraanaya</div>
      <span style={{ fontFamily: "'Dancing Script', cursive", fontSize: size, background: 'linear-gradient(90deg, #92FFF7, #74B3FF, #5F42C9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 0 15px rgba(146,255,247,0.4))' }}>taraanaya</span>
    </div>
  </div>
);

// ============================================
// VIRAL CATCHY REEL - Emotional Animated Design
// ============================================
const Reel = ({ hook, image, music, reelRef }) => {
  return (
    <div ref={reelRef} style={{ position: 'relative', width: '100%', maxWidth: 300, margin: '0 auto', aspectRatio: '9/16', borderRadius: 16, overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.3)', background: '#fff' }}>
      
      {/* Beautiful Animated Background Image */}
      <motion.div 
        style={{ position: 'absolute', inset: -10, zIndex: 1 }}
        animate={{ scale: [1, 1.05, 1.02, 1.04, 1] }}
        transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
      >
        <img 
          src={image} 
          alt="" 
          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
        />
      </motion.div>
      
      {/* Light overlay for text readability */}
      <div style={{ 
        position: 'absolute', 
        inset: 0, 
        zIndex: 2, 
        background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.05) 30%, rgba(0,0,0,0.1) 60%, rgba(0,0,0,0.3) 100%)' 
      }} />
      
      {/* Emotional Breathing Glow - Center */}
      <motion.div 
        style={{ 
          position: 'absolute', 
          width: 250, 
          height: 250, 
          borderRadius: '50%', 
          background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, rgba(255,200,220,0.15) 30%, transparent 70%)', 
          top: '30%', 
          left: '50%', 
          transform: 'translateX(-50%)', 
          zIndex: 3, 
          filter: 'blur(40px)' 
        }} 
        animate={{ 
          scale: [1, 1.3, 1, 1.2, 1],
          opacity: [0.4, 0.7, 0.4, 0.6, 0.4]
        }} 
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }} 
      />
      
      {/* Emotional Rising Light Particles */}
      {[...Array(6)].map((_, i) => (
        <motion.div 
          key={`emotion${i}`}
          style={{ 
            position: 'absolute', 
            width: 4 + i * 2, 
            height: 4 + i * 2, 
            borderRadius: '50%', 
            background: 'rgba(255,255,255,0.6)',
            boxShadow: '0 0 10px rgba(255,255,255,0.4)',
            left: `${20 + i * 12}%`, 
            bottom: '-5%',
            zIndex: 4
          }}
          animate={{ 
            y: [0, -500],
            opacity: [0, 0.8, 0.6, 0],
            scale: [0.5, 1, 0.8, 0.3]
          }}
          transition={{ 
            duration: 8 + i * 2, 
            repeat: Infinity, 
            delay: i * 1.5,
            ease: 'easeOut'
          }}
        />
      ))}
      
      {/* Soft Aurora Wave - Top */}
      <motion.div 
        style={{ 
          position: 'absolute', 
          width: '200%', 
          height: '30%', 
          top: '5%', 
          left: '-50%', 
          background: 'linear-gradient(90deg, transparent, rgba(255,200,220,0.1), rgba(200,180,255,0.15), rgba(180,220,255,0.1), transparent)', 
          filter: 'blur(30px)', 
          zIndex: 3 
        }} 
        animate={{ 
          x: ['-20%', '20%', '-20%'],
          opacity: [0.3, 0.6, 0.3]
        }} 
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }} 
      />
      
      {/* Heartbeat Pulse Effect - Behind text */}
      <motion.div 
        style={{ 
          position: 'absolute', 
          width: 180, 
          height: 180, 
          borderRadius: '50%', 
          border: '1px solid rgba(255,255,255,0.1)',
          top: '35%', 
          left: '50%', 
          transform: 'translateX(-50%)', 
          zIndex: 3
        }} 
        animate={{ 
          scale: [1, 1.5, 1],
          opacity: [0.3, 0, 0.3]
        }} 
        transition={{ duration: 3, repeat: Infinity, ease: 'easeOut' }} 
      />
      <motion.div 
        style={{ 
          position: 'absolute', 
          width: 180, 
          height: 180, 
          borderRadius: '50%', 
          border: '1px solid rgba(255,255,255,0.15)',
          top: '35%', 
          left: '50%', 
          transform: 'translateX(-50%)', 
          zIndex: 3
        }} 
        animate={{ 
          scale: [1, 1.8, 1],
          opacity: [0.2, 0, 0.2]
        }} 
        transition={{ duration: 3, repeat: Infinity, ease: 'easeOut', delay: 0.5 }} 
      />
      
      {/* Gentle Shimmer Effect */}
      <motion.div 
        style={{ 
          position: 'absolute', 
          width: '150%', 
          height: '100%', 
          top: 0, 
          left: '-25%', 
          background: 'linear-gradient(110deg, transparent 40%, rgba(255,255,255,0.08) 45%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 55%, transparent 60%)', 
          zIndex: 5 
        }} 
        animate={{ 
          x: ['-100%', '100%']
        }} 
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', repeatDelay: 3 }} 
      />

      {/* Content */}
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', zIndex: 10 }}>
        
        {/* Header - Minimal Logo */}
        <motion.div 
          style={{ paddingTop: 18, textAlign: 'center' }} 
          initial={{ opacity: 0, y: -10 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.5 }}
        >
          <Stars />
          <div style={{ marginTop: 4 }}><Logo size={18} /></div>
        </motion.div>

        {/* HOOK TEXT - Clean, Consistent & Elegant */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 28px' }}>
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.p 
              style={{ 
                fontFamily: "'DM Sans', 'Inter', -apple-system, sans-serif", 
                fontSize: 19, 
                fontWeight: 500, 
                color: '#1a1a2e',
                textAlign: 'center', 
                lineHeight: 1.55, 
                textShadow: '0 1px 12px rgba(255,255,255,0.7)',
                whiteSpace: 'pre-line', 
                letterSpacing: '-0.01em'
              }}
              animate={{
                textShadow: [
                  '0 1px 12px rgba(255,255,255,0.7)',
                  '0 1px 20px rgba(255,255,255,0.9)',
                  '0 1px 12px rgba(255,255,255,0.7)'
                ]
              }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              {hook}
            </motion.p>
          </motion.div>
        </div>

        {/* Music indicator */}
        <motion.div 
          style={{ padding: '0 20px 22px' }} 
          initial={{ opacity: 0, y: 15 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
            <motion.div 
              style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(26,26,46,0.5)' }}
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <p style={{ fontSize: 8, color: 'rgba(26,26,46,0.6)' }}>{music}</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// ============================================
// CAROUSEL - Beautiful Image Background Design
// ============================================
const Carousel = ({ slides, image, currentSlide = 0, setCurrentSlide }) => {
  const [i, setI] = useState(currentSlide);
  const navigate = useNavigate();
  const s = slides[i];
  
  // Sync with parent state for download all
  React.useEffect(() => {
    if (setCurrentSlide) {
      setI(currentSlide);
    }
  }, [currentSlide, setCurrentSlide]);
  
  const handleSetSlide = (idx) => {
    setI(idx);
    if (setCurrentSlide) setCurrentSlide(idx);
  };
  
  return (
    <div style={{ maxWidth: 340, margin: '0 auto', borderRadius: 20, overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.5)', background: '#0a0e17' }}>
      <div style={{ position: 'relative', aspectRatio: '4/5' }}>
        
        {/* Beautiful Image Background */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
          <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4 }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.6) 100%)' }} />
        </div>
        
        <AnimatePresence mode="wait">
          <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'absolute', inset: 0, zIndex: 10 }}>
            
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
              
              <Stars />
              
              {s.type === 'cover' ? (
                <>
                  <div style={{ marginTop: 4 }}><Logo size={20} /></div>
                  <p style={{ marginTop: 4, marginBottom: 24, fontSize: 6, letterSpacing: '0.2em', color: 'rgba(226,232,240,0.35)' }}>DECISION INTELLIGENCE</p>
                  <h2 style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontSize: 22, fontWeight: 300, color: 'rgba(255,255,255,0.92)', textAlign: 'center', lineHeight: 1.5, whiteSpace: 'pre-line', textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}>{s.title}</h2>
                  <div style={{ width: 35, height: 1.5, background: 'linear-gradient(90deg, #92FFF7, #5F42C9)', margin: '20px 0', borderRadius: 1 }} />
                  <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em' }}>SWIPE →</p>
                </>
              ) : s.type === 'point' ? (
                <>
                  <p style={{ fontSize: 40, fontWeight: 200, color: 'rgba(146,255,247,0.08)', marginTop: 8, marginBottom: -12 }}>{s.num}</p>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontSize: 18, fontWeight: 400, color: 'rgba(255,255,255,0.9)', textAlign: 'center', lineHeight: 1.5, marginTop: 16, textShadow: '0 2px 15px rgba(0,0,0,0.5)' }}>{s.title}</h3>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textAlign: 'center', lineHeight: 1.75, marginTop: 12, maxWidth: 230, fontWeight: 300 }}>{s.desc}</p>
                </>
              ) : (
                <>
                  <Logo size={20} />
                  <div style={{ width: 25, height: 1, background: 'linear-gradient(90deg, transparent, rgba(146,255,247,0.35), transparent)', margin: '16px 0' }} />
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', textAlign: 'center', lineHeight: 1.7, whiteSpace: 'pre-line', marginBottom: 16, textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>{s.text}</p>
                  <p style={{ marginTop: 12, fontSize: 9, color: 'rgba(255,255,255,0.25)' }}>taraanaya.com</p>
                </>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
        
        {/* Nav */}
        {slides.length > 1 && (
          <>
            <button onClick={() => handleSetSlide((i - 1 + slides.length) % slides.length)} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20 }}><ChevronLeft size={14} /></button>
            <button onClick={() => handleSetSlide((i + 1) % slides.length)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20 }}><ChevronRight size={14} /></button>
          </>
        )}
        
        <div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 4, zIndex: 20 }}>
          {slides.map((_, idx) => (
            <button key={idx} onClick={() => handleSetSlide(idx)} style={{ width: idx === i ? 16 : 4, height: 4, borderRadius: 2, background: idx === i ? '#92FFF7' : 'rgba(255,255,255,0.25)', border: 'none', cursor: 'pointer', transition: 'all 0.3s' }} />
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================
// CONTENT DATA - VIRAL HOOKS + Balanced Subtle Backgrounds
// ============================================
const data = {
  relationshipAnxiety: {
    name: "Relationship Anxiety",
    image: "https://static.prod-images.emergentagent.com/jobs/7ecb9e60-7508-4ea3-810d-6c0aacd99797/images/6605a48973d26b99e2b8d912a0aeb6330456dcd6e2b340b300de0cd9419c8e49.png",
    reel: {
      hook: "You love him.\n\nBut why do you\nstill feel alone?",
      music: 'Echoes of You - Emotional Piano'
    },
    musicNote: 'Search: "Echoes of You piano" or "sad piano instrumental" on Instagram audio',
    hashtags: '#relationshipadvice #love #astrology #zodiac #emotionalhealth #selflove #toxicrelationships #knowyourworth #dating #soulmate #twinflame #venusretrograde #cosmicguidance #tarot #spirituality',
    caption: "That feeling of emptiness even when you are together? Your stars have the answer.\n\nSave this if it resonates. Tag someone who needs to see this.\n\nDM 'RELATIONSHIP' for your personalized reading.",
    carousel: [
      { type: 'cover', title: "5 Signs\nYou're Emotionally\nUnseen" },
      { type: 'point', num: '01', title: 'You explain yourself constantly', desc: 'Your words need repeating. Your feelings need defending.' },
      { type: 'point', num: '02', title: 'Their silence hurts more', desc: 'You share your heart. They check their phone.' },
      { type: 'point', num: '03', title: 'You feel lonely together', desc: 'Beside them feels emptier than being alone.' },
      { type: 'point', num: '04', title: 'Small things cut deep', desc: 'A forgotten text. A dismissive look.' },
      { type: 'point', num: '05', title: 'You doubt your worth', desc: 'Am I asking for too much? You\'re not.' },
      { type: 'cta', text: 'This pattern isn\'t random.\nYour timeline holds answers.' }
    ]
  },
  marriedConflict: {
    name: "Married in Conflict",
    image: "https://static.prod-images.emergentagent.com/jobs/7ecb9e60-7508-4ea3-810d-6c0aacd99797/images/8c18c3fc1659bd78af525ebddcac5cac60337ee5d217526785a28823a7f13b10.png",
    reel: {
      hook: "When did\n'I love you'\nbecome routine?",
      music: 'Faded Memories - Soft Piano'
    },
    musicNote: 'Search: "Faded Memories piano" or "melancholic piano" on Instagram audio',
    hashtags: '#marriage #marriageadvice #relationships #love #husbandwife #couplegoals #marriedlife #astrology #zodiac #venus #relationshipgoals #soulmate #compatibility #divinetiming #cosmicconnection',
    caption: "Every marriage goes through seasons. Knowing which season you are in changes everything.\n\nSave for later. Share with someone who might need this.\n\nDM 'MARRIAGE' for guidance.",
    carousel: [
      { type: 'cover', title: "Marriage\ndoesn't fail\nin one day" },
      { type: 'point', num: '01', title: 'Silence grows', desc: 'Words feel risky. So you stop.' },
      { type: 'point', num: '02', title: 'Resentment builds', desc: 'Small disappointments become invisible walls.' },
      { type: 'point', num: '03', title: 'Conversations become logistics', desc: '"Kids ate." Nothing deeper.' },
      { type: 'point', num: '04', title: 'Touch disappears', desc: 'When did the goodnight kiss stop?' },
      { type: 'point', num: '05', title: 'Roommates, not partners', desc: 'Together in house. Strangers in heart.' },
      { type: 'cta', text: 'Every relationship has phases.\nKnowing yours changes everything.' }
    ]
  },
  recentlyBrokenUp: {
    name: "Moving On",
    image: "https://static.prod-images.emergentagent.com/jobs/16af9a27-8a3a-4cf8-ab9c-4ab0d36743a7/images/81a083bfec92f1e084c605a6d8b69c15329fa02814a191fc47d26e6475f0bfea.png",
    reel: {
      hook: "Some chapters end\nso better ones\ncan begin.\n\nYours is coming.",
      music: 'New Dawn Rising - Hopeful Piano'
    },
    musicNote: 'Search: "hopeful piano instrumental" or "new beginning music" on Instagram audio',
    hashtags: '#newbeginnings #selflove #healing #movingforward #astrology #tarot #zodiac #universe #manifestation #letgo #growth #selfcare #spiritualjourney #divineguidance #trusttheprocess',
    caption: "Every ending is a new beginning written in the stars. Your next chapter is already being written. Save this if you needed to hear it today.\n\nDM 'NEWSTART' for your timeline.",
    carousel: [
      { type: 'cover', title: "The hardest part\nisn't the goodbye" },
      { type: 'point', num: '01', title: 'It\'s the morning after', desc: 'Reaching for your phone. Then remembering.' },
      { type: 'point', num: '02', title: 'It\'s the songs', desc: 'Every melody reopens a wound.' },
      { type: 'point', num: '03', title: 'It\'s the what-ifs', desc: 'What if I gave more? The spiral begins.' },
      { type: 'point', num: '04', title: 'It\'s being alone', desc: 'You were "us." Now who is "you"?' },
      { type: 'point', num: '05', title: 'It\'s trusting again', desc: 'Believing someone new won\'t leave too.' },
      { type: 'cta', text: 'Some endings are redirections.\nYour story is still being written.' }
    ]
  },
  marriageDelay: {
    name: "Marriage Delay",
    image: "https://static.prod-images.emergentagent.com/jobs/7ecb9e60-7508-4ea3-810d-6c0aacd99797/images/581315525c2018d9aca0acd98cc976c08c470fed1c7cdccde101059f33022a0c.png",
    reel: {
      hook: "Everyone's getting\nmarried.\n\nAnd you're wondering\nwhat's wrong with you.",
      music: 'Waiting for You - Gentle Piano'
    },
    musicNote: 'Search: "waiting piano" or "patience instrumental" on Instagram audio',
    hashtags: '#marriage #single #wedding #astrology #zodiac #divinetiming #soulmate #lovelife #manifestation #universe #patience #rightperson #marriagetiming #kundli #horoscope',
    caption: "Nothing is wrong with you. Your timeline is just different. And different is beautiful.\n\nSave this if you needed this reminder today.\n\nDM 'TIMING' to see your marriage window.",
    carousel: [
      { type: 'cover', title: "Your timing\nis not wrong" },
      { type: 'point', num: '01', title: 'Comparison is cruel', desc: 'Every engagement post feels like a reminder.' },
      { type: 'point', num: '02', title: 'Family questions cut deep', desc: '"When?" Each question a small wound.' },
      { type: 'point', num: '03', title: 'Self-doubt at 2am', desc: 'Am I too picky? Too much? Too little?' },
      { type: 'point', num: '04', title: 'Weddings feel heavy', desc: 'You smile. Then drive home alone.' },
      { type: 'point', num: '05', title: 'Your story isn\'t late', desc: 'Some flowers bloom in summer. Both beautiful.' },
      { type: 'cta', text: 'Your timeline was written\nbefore the questions began.' }
    ]
  },
  vergeSeparation: {
    name: "Verge of Separation",
    image: "https://static.prod-images.emergentagent.com/jobs/16af9a27-8a3a-4cf8-ab9c-4ab0d36743a7/images/b81bbef5cf10be3fa1969d4267b12e0069561912401403e1ec40d31e4190fb0b.png",
    reel: {
      hook: "One more fight.\n\nAnd this time,\nyou might actually leave.",
      music: 'Crossroads - Dramatic Piano'
    },
    musicNote: 'Search: "dramatic piano" or "emotional decision music" on Instagram audio',
    hashtags: '#marriage #relationships #decision #astrology #zodiac #clarity #guidance #divineguidance #lifedecisions #courage #strength #movingon #stayorgo #cosmicguidance #universe',
    caption: "Some storms are meant to be weathered. Some are meant to be walked away from. The stars can help you see which one this is.\n\nSave this. You might need it.\n\nDM 'CLARITY' for guidance.",
    carousel: [
      { type: 'cover', title: "Is this the end?\n\nOr just a chapter?" },
      { type: 'point', num: '01', title: 'The fights feel endless', desc: 'Same arguments. Different days.' },
      { type: 'point', num: '02', title: 'You\'ve stopped explaining', desc: 'What\'s the point? They don\'t hear.' },
      { type: 'point', num: '03', title: 'The thought comes often', desc: '"What if I just... left?"' },
      { type: 'point', num: '04', title: 'Something holds you back', desc: 'A memory. A version of them you believe in.' },
      { type: 'point', num: '05', title: 'This crossroad matters', desc: 'The decision shapes years. Don\'t rush.' },
      { type: 'cta', text: 'Some storms are meant to be weathered.\nSome are meant to be escaped.' }
    ]
  }
};

// ============================================
// MAIN PAGE
// ============================================
const SocialMediaContent = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('reels');
  const [icp, setIcp] = useState('relationshipAnxiety');
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState('');
  const [carouselSlide, setCarouselSlide] = useState(0);
  const contentRef = useRef(null);
  const reelRef = useRef(null);
  
  const icps = [
    { k: 'relationshipAnxiety', l: 'Relationship Anxiety' },
    { k: 'marriedConflict', l: 'Married Conflict' },
    { k: 'recentlyBrokenUp', l: 'Moving On' },
    { k: 'marriageDelay', l: 'Marriage Delay' },
    { k: 'vergeSeparation', l: 'Separation' }
  ];

  const c = data[icp];
  
  // Copy to clipboard function
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const downloadElement = async (element, filename) => {
    try {
      const canvas = await html2canvas(element, {
        backgroundColor: '#0a0e17',
        scale: 3,
        useCORS: true,
        allowTaint: true,
        logging: false,
        onclone: (clonedDoc, clonedElement) => {
          // Find all images and ensure they're loaded
          const images = clonedElement.querySelectorAll('img');
          images.forEach(img => {
            img.crossOrigin = 'anonymous';
          });
        }
      });
      const link = document.createElement('a');
      link.download = filename;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
      return true;
    } catch (err) {
      console.error('Download failed:', err);
      return false;
    }
  };

  // Convert image to base64 for download
  const getImageAsBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.error('Failed to convert image:', e);
      return url;
    }
  };

  // Direct canvas drawing for perfect download
  const handleSingleDownload = async (icpKey) => {
    const icpData = data[icpKey];
    
    try {
      // Fetch image through backend proxy to avoid CORS
      const proxyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/image-proxy/fetch?url=${encodeURIComponent(icpData.image)}`;
      console.log('Fetching from:', proxyUrl);
      const response = await fetch(proxyUrl);
      const result = await response.json();
      console.log('Result:', result);
      
      if (!result.success) throw new Error('Failed to fetch image');
      
      // Wait for image to load
      const bgImage = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Image load failed'));
        img.src = result.data;
      });
      
      // Create canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Instagram Reel dimensions (9:16 aspect ratio)
      canvas.width = 1080;
      canvas.height = 1920;
      
      // Draw background image (cover entire canvas)
      const scale = Math.max(canvas.width / bgImage.width, canvas.height / bgImage.height);
      const x = (canvas.width - bgImage.width * scale) / 2;
      const y = (canvas.height - bgImage.height * scale) / 2;
      ctx.drawImage(bgImage, x, y, bgImage.width * scale, bgImage.height * scale);
      
      // Add very subtle overlay for text readability
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, 'rgba(0,0,0,0.12)');
      gradient.addColorStop(0.3, 'rgba(0,0,0,0.03)');
      gradient.addColorStop(0.7, 'rgba(0,0,0,0.05)');
      gradient.addColorStop(1, 'rgba(0,0,0,0.2)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw stars arc
      const starPositions = [
        { x: canvas.width/2 - 140, y: 130, size: 14 },
        { x: canvas.width/2 - 70, y: 90, size: 18 },
        { x: canvas.width/2, y: 75, size: 22 },
        { x: canvas.width/2 + 70, y: 90, size: 18 },
        { x: canvas.width/2 + 140, y: 130, size: 14 }
      ];
      
      const starColors = ['#92FFF7', '#82D9F9', '#74B3FF', '#6A7BE8', '#5F42C9'];
      
      // Draw arc line first
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(116, 179, 255, 0.6)';
      ctx.lineWidth = 3;
      ctx.moveTo(starPositions[0].x, starPositions[0].y);
      ctx.quadraticCurveTo(canvas.width/2, 50, starPositions[4].x, starPositions[4].y);
      ctx.stroke();
      
      // Draw 4-point stars
      starPositions.forEach((pos, i) => {
        ctx.fillStyle = starColors[i];
        ctx.beginPath();
        const s = pos.size;
        ctx.moveTo(pos.x, pos.y - s);
        ctx.lineTo(pos.x + s * 0.35, pos.y - s * 0.35);
        ctx.lineTo(pos.x + s, pos.y);
        ctx.lineTo(pos.x + s * 0.35, pos.y + s * 0.35);
        ctx.lineTo(pos.x, pos.y + s);
        ctx.lineTo(pos.x - s * 0.35, pos.y + s * 0.35);
        ctx.lineTo(pos.x - s, pos.y);
        ctx.lineTo(pos.x - s * 0.35, pos.y - s * 0.35);
        ctx.closePath();
        ctx.fill();
      });
      
      // Draw taraanaya logo text with gradient
      ctx.textAlign = 'center';
      ctx.font = "italic 52px Georgia, serif";
      const logoGradient = ctx.createLinearGradient(canvas.width/2 - 100, 0, canvas.width/2 + 100, 0);
      logoGradient.addColorStop(0, '#92FFF7');
      logoGradient.addColorStop(0.5, '#74B3FF');
      logoGradient.addColorStop(1, '#5F42C9');
      ctx.fillStyle = logoGradient;
      ctx.fillText('taraanaya', canvas.width / 2, 210);
      
      // Draw hook text
      const hookText = icpData.reel.hook;
      const lines = hookText.split('\n');
      ctx.font = "500 58px Arial, sans-serif";
      ctx.textAlign = 'center';
      ctx.fillStyle = '#1a1a2e';
      
      // Calculate vertical center for text
      const lineHeight = 85;
      const nonEmptyLines = lines.filter(l => l.trim());
      const totalHeight = nonEmptyLines.length * lineHeight;
      let currentY = (canvas.height - totalHeight) / 2 + 100;
      
      // Add text shadow effect
      ctx.shadowColor = 'rgba(255,255,255,0.7)';
      ctx.shadowBlur = 15;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 2;
      
      lines.forEach((line) => {
        if (line.trim()) {
          ctx.fillText(line, canvas.width / 2, currentY);
          currentY += lineHeight;
        } else {
          currentY += 30;
        }
      });
      
      // Reset shadow
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      
      // Download
      const link = document.createElement('a');
      const icpName = icpData.name.replace(/\s+/g, '_').toLowerCase();
      link.download = `taraanaya_${icpName}_reel.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
      
    } catch (e) {
      console.error('Download error:', e);
      alert('Failed to download. Please try again.');
    }
  };

  // Download Cover Page - Matching reference image exactly
  const handleDownloadCover = async () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Square format
      canvas.width = 1080;
      canvas.height = 1080;
      
      // Dark blue background matching reference
      ctx.fillStyle = '#03071e';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Vertical gradient for depth
      const bgGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      bgGradient.addColorStop(0, '#03071e');
      bgGradient.addColorStop(0.5, '#0a1128');
      bgGradient.addColorStop(1, '#03071e');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Left blue glow effect
      const leftGlow = ctx.createRadialGradient(100, 200, 0, 100, 200, 350);
      leftGlow.addColorStop(0, 'rgba(0,150,200,0.25)');
      leftGlow.addColorStop(1, 'transparent');
      ctx.fillStyle = leftGlow;
      ctx.fillRect(0, 0, 500, 500);
      
      // Right blue glow effect
      const rightGlow = ctx.createRadialGradient(950, 150, 0, 950, 150, 300);
      rightGlow.addColorStop(0, 'rgba(0,180,220,0.2)');
      rightGlow.addColorStop(1, 'transparent');
      ctx.fillStyle = rightGlow;
      ctx.fillRect(600, 0, 480, 400);
      
      // Scattered stars with glow
      const stars = [
        { x: 130, y: 65, r: 4 },
        { x: 810, y: 85, r: 3 },
        { x: 950, y: 160, r: 2.5 },
        { x: 55, y: 270, r: 3 },
        { x: 990, y: 380, r: 2.5 },
        { x: 55, y: 540, r: 3 },
        { x: 920, y: 700, r: 2.5 },
        { x: 160, y: 810, r: 3 },
      ];
      
      stars.forEach(s => {
        // Glow
        const glow = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r * 3);
        glow.addColorStop(0, 'rgba(255,255,255,0.6)');
        glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow;
        ctx.fillRect(s.x - s.r * 3, s.y - s.r * 3, s.r * 6, s.r * 6);
        
        // Star point
        ctx.fillStyle = 'rgba(255,255,255,0.8)';
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      });
      
      ctx.textAlign = 'center';
      
      // Stars arc - matching the component
      const starPositions = [
        { x: canvas.width/2 - 140, y: 100, size: 14 },
        { x: canvas.width/2 - 70, y: 65, size: 18 },
        { x: canvas.width/2, y: 50, size: 22 },
        { x: canvas.width/2 + 70, y: 65, size: 18 },
        { x: canvas.width/2 + 140, y: 100, size: 14 }
      ];
      
      const starColors = ['#92FFF7', '#82D9F9', '#74B3FF', '#6A7BE8', '#5F42C9'];
      
      // Arc line
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(116, 179, 255, 0.6)';
      ctx.lineWidth = 3;
      ctx.moveTo(starPositions[0].x, starPositions[0].y);
      ctx.quadraticCurveTo(canvas.width/2, 25, starPositions[4].x, starPositions[4].y);
      ctx.stroke();
      
      // 4-point stars
      starPositions.forEach((pos, i) => {
        ctx.fillStyle = starColors[i];
        ctx.beginPath();
        const s = pos.size;
        ctx.moveTo(pos.x, pos.y - s);
        ctx.lineTo(pos.x + s * 0.35, pos.y - s * 0.35);
        ctx.lineTo(pos.x + s, pos.y);
        ctx.lineTo(pos.x + s * 0.35, pos.y + s * 0.35);
        ctx.lineTo(pos.x, pos.y + s);
        ctx.lineTo(pos.x - s * 0.35, pos.y + s * 0.35);
        ctx.lineTo(pos.x - s, pos.y);
        ctx.lineTo(pos.x - s * 0.35, pos.y - s * 0.35);
        ctx.closePath();
        ctx.fill();
      });
      
      // taraanaya logo - cyan, cursive
      ctx.font = "italic 70px Georgia, serif";
      ctx.fillStyle = '#00e5ff';
      ctx.shadowColor = 'rgba(0,229,255,0.4)';
      ctx.shadowBlur = 20;
      ctx.fillText('taraanaya', canvas.width / 2, 180);
      ctx.shadowBlur = 0;
      
      // Main headline - serif font, white
      let y = 310;
      ctx.font = "400 95px Georgia, 'Cormorant Garamond', serif";
      ctx.fillStyle = '#FFFFFF';
      ctx.fillText('Decision', canvas.width / 2, y);
      
      y += 100;
      ctx.fillText('Intelligence', canvas.width / 2, y);
      
      // Platform - bolder, cyan
      y += 105;
      ctx.font = "600 95px Georgia, 'Cormorant Garamond', serif";
      ctx.fillStyle = '#00e5ff';
      ctx.fillText('Platform', canvas.width / 2, y);
      
      // powered by astrology - italic
      y += 75;
      ctx.font = "italic 38px Georgia, serif";
      ctx.fillStyle = '#5da8c7';
      ctx.fillText('powered by astrology', canvas.width / 2, y);
      
      // Description lines
      y += 75;
      ctx.font = "400 32px -apple-system, sans-serif";
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillText('Mapping real life events to', canvas.width / 2, y);
      y += 42;
      ctx.fillText('Vedic astrology', canvas.width / 2, y);
      
      y += 55;
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fillText('Helping you make', canvas.width / 2, y);
      y += 42;
      ctx.fillText('better decisions', canvas.width / 2, y);
      
      // Draw zodiac wheel at bottom
      const wheelCenterX = canvas.width / 2;
      const wheelCenterY = canvas.height + 100;
      const wheelRadius = 280;
      
      // Outer arcs
      ctx.strokeStyle = 'rgba(0,180,200,0.6)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(wheelCenterX, wheelCenterY, wheelRadius, Math.PI, 0);
      ctx.stroke();
      
      ctx.strokeStyle = 'rgba(0,180,200,0.4)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(wheelCenterX, wheelCenterY, wheelRadius - 30, Math.PI, 0);
      ctx.stroke();
      
      ctx.strokeStyle = 'rgba(0,180,200,0.3)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(wheelCenterX, wheelCenterY, wheelRadius - 55, Math.PI, 0);
      ctx.stroke();
      
      // Radial lines
      for (let i = 0; i < 12; i++) {
        const angle = Math.PI + (Math.PI * (i + 1)) / 13;
        const x1 = wheelCenterX + Math.cos(angle) * (wheelRadius - 55);
        const y1 = wheelCenterY + Math.sin(angle) * (wheelRadius - 55);
        const x2 = wheelCenterX + Math.cos(angle) * 80;
        const y2 = wheelCenterY + Math.sin(angle) * 80;
        
        ctx.strokeStyle = 'rgba(0,180,200,0.25)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
      
      // Center sun circles
      ctx.strokeStyle = 'rgba(0,200,220,0.5)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(wheelCenterX, wheelCenterY, 70, 0, Math.PI * 2);
      ctx.stroke();
      
      ctx.strokeStyle = 'rgba(0,200,220,0.4)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(wheelCenterX, wheelCenterY, 50, 0, Math.PI * 2);
      ctx.stroke();
      
      ctx.fillStyle = 'rgba(0,200,220,0.15)';
      ctx.beginPath();
      ctx.arc(wheelCenterX, wheelCenterY, 30, 0, Math.PI * 2);
      ctx.fill();
      
      // Sun rays
      for (let i = 0; i < 12; i++) {
        const angle = (Math.PI * 2 * i) / 12;
        const x1 = wheelCenterX + Math.cos(angle) * 35;
        const y1 = wheelCenterY + Math.sin(angle) * 35;
        const x2 = wheelCenterX + Math.cos(angle) * 55;
        const y2 = wheelCenterY + Math.sin(angle) * 55;
        
        ctx.strokeStyle = 'rgba(0,200,220,0.6)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
      
      // Zodiac labels
      ctx.font = "18px sans-serif";
      ctx.fillStyle = 'rgba(0,180,200,0.5)';
      ctx.fillText('PISCES', 280, 940);
      ctx.fillText('AQUARIUS', 380, 890);
      ctx.fillText('CAPRICORN', 480, 865);
      ctx.fillText('ARIES', 620, 865);
      ctx.fillText('TAURUS', 720, 890);
      ctx.fillText('GEMINI', 820, 940);
      
      // Download
      const link = document.createElement('a');
      link.download = 'taraanaya_cover.png';
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
      
    } catch (e) {
      console.error('Cover download error:', e);
      alert('Failed to download cover. Please try again.');
    }
  };

  const handleDownload = async () => {
    if (!contentRef.current || downloading) return;
    setDownloading(true);
    setDownloadProgress('Preparing...');
    await new Promise(r => setTimeout(r, 500));
    await downloadElement(contentRef.current, `taraanaya_${icp}_${tab}.png`);
    setDownloadProgress('');
    setDownloading(false);
  };

  const handleDownloadAll = async () => {
    if (downloading) return;
    setDownloading(true);
    
    const allKeys = Object.keys(data);
    let totalItems = 0;
    
    // Count total items
    for (const key of allKeys) {
      totalItems += 1; // reel
      totalItems += data[key].carousel.length; // carousel slides
    }
    
    let currentItem = 0;
    
    for (const key of allKeys) {
      // Switch to this ICP and reels tab
      setIcp(key);
      setTab('reels');
      await new Promise(r => setTimeout(r, 800));
      
      currentItem++;
      setDownloadProgress(`Downloading ${currentItem}/${totalItems} - ${key} reel`);
      
      if (contentRef.current) {
        await downloadElement(contentRef.current, `taraanaya_${key}_reel.png`);
      }
      await new Promise(r => setTimeout(r, 500));
      
      // Now download all carousel slides
      setTab('carousels');
      await new Promise(r => setTimeout(r, 500));
      
      const carouselSlides = data[key].carousel.length;
      for (let slideIdx = 0; slideIdx < carouselSlides; slideIdx++) {
        setCarouselSlide(slideIdx);
        await new Promise(r => setTimeout(r, 600));
        
        currentItem++;
        setDownloadProgress(`Downloading ${currentItem}/${totalItems} - ${key} slide ${slideIdx + 1}`);
        
        if (contentRef.current) {
          await downloadElement(contentRef.current, `taraanaya_${key}_carousel_${slideIdx + 1}.png`);
        }
        await new Promise(r => setTimeout(r, 400));
      }
    }
    
    setDownloadProgress('');
    setDownloading(false);
    setCarouselSlide(0);
    alert('All content downloaded successfully!');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0e17' }} data-testid="social-media-content-page">

      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(10,14,23,0.95)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px' }}>
          <button onClick={() => navigate(-1)} style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ArrowLeft size={16} color="#E8E8E8" />
          </button>
          <div style={{ textAlign: 'center' }}>
            <Stars />
            <p style={{ marginTop: 2, fontSize: 6, letterSpacing: '0.2em', color: 'rgba(255,255,255,0.25)' }}>CONTENT STUDIO</p>
          </div>
          <button onClick={handleDownload} disabled={downloading} style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(146,255,247,0.1)', border: '1px solid rgba(146,255,247,0.2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: downloading ? 0.5 : 1 }}>
            <Download size={16} color="#92FFF7" />
          </button>
        </div>
        
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, padding: '0 14px 10px', alignItems: 'center' }}>
          {['reels', 'carousels', 'cover'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 16px', borderRadius: 16, fontSize: 11, fontWeight: 600, background: tab === t ? 'linear-gradient(135deg, rgba(146,255,247,0.1), rgba(95,66,201,0.1))' : 'transparent', color: tab === t ? '#92FFF7' : 'rgba(255,255,255,0.3)', border: tab === t ? '1px solid rgba(146,255,247,0.2)' : '1px solid transparent', cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
          ))}
        </div>
      </div>

      {/* ICP */}
      <div style={{ padding: '12px 14px 0' }}>
        <p style={{ fontSize: 7, letterSpacing: '0.2em', color: 'rgba(255,255,255,0.2)', marginBottom: 8 }}>AUDIENCE</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {icps.map(x => (
            <button key={x.k} onClick={() => setIcp(x.k)} style={{ padding: '6px 11px', borderRadius: 12, fontSize: 10, fontWeight: 500, background: icp === x.k ? 'rgba(255,255,255,0.08)' : 'transparent', color: icp === x.k ? '#fff' : 'rgba(255,255,255,0.3)', border: icp === x.k ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}>{x.l}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '20px 14px 20px' }}>
        <AnimatePresence mode="wait">
          {tab === 'reels' ? (
            <motion.div key="r" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div ref={contentRef}>
                <Reel {...c.reel} image={c.image} reelRef={reelRef} />
              </div>
              {/* Individual Download Button */}
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                <button 
                  onClick={() => handleSingleDownload(icp)}
                  style={{ 
                    padding: '10px 20px', 
                    background: 'linear-gradient(135deg, rgba(146,255,247,0.15), rgba(95,66,201,0.15))', 
                    border: '1px solid rgba(146,255,247,0.3)', 
                    borderRadius: 20, 
                    color: '#92FFF7', 
                    fontSize: 11, 
                    fontWeight: 600, 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <Download size={14} />
                  Download This Reel
                </button>
              </div>
            </motion.div>
          ) : tab === 'carousels' ? (
            <motion.div key="c" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div ref={contentRef}>
                <Carousel slides={c.carousel} image={c.image} currentSlide={carouselSlide} setCurrentSlide={setCarouselSlide} />
              </div>
            </motion.div>
          ) : (
            /* COVER TAB - Clean, simple, wheel at bottom */
            <motion.div key="cover" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {/* Cover Preview - Clean design */}
                <div style={{
                  width: '100%',
                  maxWidth: 340,
                  aspectRatio: '1/1',
                  borderRadius: 16,
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(180deg, #0a1628 0%, #0d1b33 50%, #101e3a 100%)',
                  boxShadow: '0 25px 80px rgba(0,0,0,0.6)'
                }}>
                  
                  {/* Gradient orbs */}
                  <div style={{
                    position: 'absolute',
                    top: -80,
                    right: -80,
                    width: 200,
                    height: 200,
                    background: 'rgba(59, 130, 246, 0.12)',
                    borderRadius: '50%',
                    filter: 'blur(60px)'
                  }} />
                  <div style={{
                    position: 'absolute',
                    bottom: -80,
                    left: -80,
                    width: 200,
                    height: 200,
                    background: 'rgba(147, 51, 234, 0.1)',
                    borderRadius: '50%',
                    filter: 'blur(60px)'
                  }} />
                  
                  {/* Small stars */}
                  {[
                    { top: '10%', left: '12%' },
                    { top: '15%', left: '85%' },
                    { top: '40%', left: '8%' },
                    { top: '45%', left: '90%' },
                  ].map((s, i) => (
                    <div key={i} style={{
                      position: 'absolute',
                      top: s.top,
                      left: s.left,
                      width: 3,
                      height: 3,
                      background: 'rgba(191, 219, 254, 0.5)',
                      borderRadius: '50%'
                    }} />
                  ))}

                  {/* Main Content - top portion only, NO intersection with wheel */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '58%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px 20px',
                    zIndex: 10
                  }}>
                    
                    {/* taraanaya logo */}
                    <div style={{
                      fontFamily: "Georgia, serif",
                      fontStyle: 'italic',
                      fontSize: 22,
                      color: '#00e5ff',
                      textShadow: '0 0 25px rgba(0,229,255,0.4)'
                    }}>
                      taraanaya
                    </div>
                    
                    {/* Main headline - same font, clean */}
                    <div style={{ marginTop: 20, textAlign: 'center' }}>
                      <div style={{
                        fontFamily: "Georgia, serif",
                        fontSize: 32,
                        fontWeight: 400,
                        color: '#FFFFFF',
                        lineHeight: 1.2
                      }}>
                        Decision
                      </div>
                      <div style={{
                        fontFamily: "Georgia, serif",
                        fontSize: 32,
                        fontWeight: 400,
                        color: '#FFFFFF',
                        lineHeight: 1.2
                      }}>
                        Intelligence
                      </div>
                      <div style={{
                        fontFamily: "Georgia, serif",
                        fontSize: 32,
                        fontWeight: 600,
                        color: '#00e5ff',
                        lineHeight: 1.2
                      }}>
                        Platform
                      </div>
                    </div>
                    
                    {/* powered by astrology */}
                    <div style={{
                      fontFamily: "Georgia, serif",
                      fontStyle: 'italic',
                      fontSize: 13,
                      color: '#60a5fa',
                      marginTop: 14
                    }}>
                      powered by astrology
                    </div>
                  </div>
                  
                  {/* Zodiac wheel at BOTTOM - half visible, no text overlap */}
                  <div style={{
                    position: 'absolute',
                    bottom: -120,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 300,
                    height: 300,
                    zIndex: 5
                  }}>
                    <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
                      {/* Circles */}
                      <circle cx="100" cy="100" r="95" fill="none" stroke="rgba(0, 180, 200, 0.4)" strokeWidth="1" />
                      <circle cx="100" cy="100" r="78" fill="none" stroke="rgba(0, 180, 200, 0.3)" strokeWidth="1" />
                      <circle cx="100" cy="100" r="60" fill="none" stroke="rgba(0, 180, 200, 0.25)" strokeWidth="1" />
                      
                      {/* 12 division lines */}
                      {[...Array(12)].map((_, i) => {
                        const angle = (i * 30) * (Math.PI / 180);
                        return (
                          <line key={i} 
                            x1={100 + 60 * Math.cos(angle)} 
                            y1={100 + 60 * Math.sin(angle)} 
                            x2={100 + 95 * Math.cos(angle)} 
                            y2={100 + 95 * Math.sin(angle)} 
                            stroke="rgba(0, 180, 200, 0.25)" 
                            strokeWidth="0.5"
                          />
                        );
                      })}
                      
                      {/* Center sun */}
                      <circle cx="100" cy="100" r="45" fill="none" stroke="rgba(0, 200, 220, 0.4)" strokeWidth="1"/>
                      <circle cx="100" cy="100" r="32" fill="rgba(0, 180, 200, 0.06)" stroke="rgba(0, 200, 220, 0.5)" strokeWidth="1"/>
                      
                      {/* Sun rays */}
                      {[...Array(16)].map((_, i) => {
                        const angle = (Math.PI * 2 * i) / 16;
                        const inner = 35;
                        const outer = i % 2 === 0 ? 50 : 42;
                        return (
                          <line key={i} 
                            x1={100 + inner * Math.cos(angle)} 
                            y1={100 + inner * Math.sin(angle)} 
                            x2={100 + outer * Math.cos(angle)} 
                            y2={100 + outer * Math.sin(angle)} 
                            stroke="rgba(0, 200, 220, 0.5)" 
                            strokeWidth={i % 2 === 0 ? "1.5" : "1"}
                          />
                        );
                      })}
                    </svg>
                  </div>
                </div>
              </div>

              {/* Download Cover Button */}
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                <button 
                  onClick={handleDownloadCover}
                  style={{ 
                    padding: '12px 24px', 
                    background: 'linear-gradient(135deg, rgba(0,229,255,0.15), rgba(0,150,200,0.1))', 
                    border: '1px solid rgba(0,229,255,0.3)', 
                    borderRadius: 20, 
                    color: '#00e5ff', 
                    fontSize: 12, 
                    fontWeight: 600, 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <Download size={16} />
                  Download Cover
                </button>
              </div>

              {/* Cover Info */}
              <div style={{ 
                marginTop: 20, 
                padding: 16, 
                background: 'rgba(255,255,255,0.02)', 
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.05)'
              }}>
                <p style={{ fontSize: 8, letterSpacing: '0.15em', color: 'rgba(0,229,255,0.6)', marginBottom: 8 }}>COVER DETAILS</p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
                  Clean cover with zodiac wheel.
                </p>
                <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ padding: '4px 10px', background: 'rgba(0,229,255,0.08)', borderRadius: 8, fontSize: 9, color: 'rgba(0,229,255,0.7)' }}>1080 × 1080</span>
                  <span style={{ padding: '4px 10px', background: 'rgba(0,229,255,0.08)', borderRadius: 8, fontSize: 9, color: 'rgba(0,229,255,0.7)' }}>PNG</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Instagram Ready Section */}
      {tab === 'reels' && c.caption && (
        <div style={{ padding: '0 14px 20px' }}>
          <div style={{ 
            background: 'rgba(255,255,255,0.03)', 
            border: '1px solid rgba(255,255,255,0.08)', 
            borderRadius: 12, 
            padding: 16 
          }}>
            <p style={{ fontSize: 8, letterSpacing: '0.15em', color: 'rgba(146,255,247,0.6)', marginBottom: 12, fontWeight: 600 }}>INSTAGRAM READY</p>
            
            {/* Caption */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>CAPTION</p>
                <button 
                  onClick={() => copyToClipboard(c.caption)}
                  style={{ 
                    padding: '4px 10px', 
                    background: 'rgba(146,255,247,0.1)', 
                    border: '1px solid rgba(146,255,247,0.2)', 
                    borderRadius: 6, 
                    color: '#92FFF7', 
                    fontSize: 9, 
                    cursor: 'pointer' 
                  }}
                >
                  Copy
                </button>
              </div>
              <p style={{ 
                fontSize: 11, 
                color: 'rgba(255,255,255,0.7)', 
                lineHeight: 1.6, 
                whiteSpace: 'pre-line',
                background: 'rgba(0,0,0,0.2)',
                padding: 12,
                borderRadius: 8
              }}>
                {c.caption}
              </p>
            </div>
            
            {/* Hashtags */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>HASHTAGS</p>
                <button 
                  onClick={() => copyToClipboard(c.hashtags)}
                  style={{ 
                    padding: '4px 10px', 
                    background: 'rgba(146,255,247,0.1)', 
                    border: '1px solid rgba(146,255,247,0.2)', 
                    borderRadius: 6, 
                    color: '#92FFF7', 
                    fontSize: 9, 
                    cursor: 'pointer' 
                  }}
                >
                  Copy
                </button>
              </div>
              <p style={{ 
                fontSize: 10, 
                color: 'rgba(116,179,255,0.8)', 
                lineHeight: 1.8,
                background: 'rgba(0,0,0,0.2)',
                padding: 12,
                borderRadius: 8,
                wordBreak: 'break-word'
              }}>
                {c.hashtags}
              </p>
            </div>
            
            {/* Music Suggestion */}
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>SUGGESTED MUSIC</p>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>{c.reel.music}</p>
              {c.musicNote && (
                <p style={{ fontSize: 9, color: 'rgba(146,255,247,0.5)', marginTop: 6, fontStyle: 'italic' }}>{c.musicNote}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Download All Button */}
      <div style={{ padding: '0 14px 40px' }}>
        <button 
          onClick={handleDownloadAll} 
          disabled={downloading}
          style={{ 
            width: '100%', 
            padding: '14px', 
            background: 'transparent', 
            border: '1px solid rgba(146,255,247,0.25)', 
            borderRadius: 12, 
            color: 'rgba(146,255,247,0.8)', 
            fontSize: 12, 
            fontWeight: 500, 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 8,
            opacity: downloading ? 0.5 : 1
          }}
        >
          <Download size={16} />
          {downloadProgress || 'Download All (5 Reels + 35 Carousel Slides)'}
        </button>
        <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', textAlign: 'center', marginTop: 8 }}>
          Downloads exact preview for all ICPs
        </p>
      </div>
    </div>
  );
};

export default SocialMediaContent;
